import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { FaCertificate, FaCheckCircle, FaHandshake, FaUserGraduate } from "react-icons/fa";
import {
  fetchAdmissions,
  fetchSiteSettings,
  fetchTestimonials,
  fetchTrades,
  submitEnquiry as postEnquiryLead,
} from "../api/api";
import { getAssetUrl } from "../utils/assets";
import Testimonials from "../components/Testimonials";
import ProgressiveImage from "../components/ui/ProgressiveImage";
import StableImage from "../components/ui/StableImage";
import ToastStack from "../components/ui/ToastStack";
import {
  btnOnGradient,
  btnOutlineOnGradient,
  btnPrimary,
  btnSecondary,
  inputField,
} from "../components/ui/saasPrimitives";
import "../styles/home.css";

const PHONE_TEL = "+919480085001";
const PHONE_DISPLAY = "+91 94800 85001";

const scrollToEnquiry = () => {
  document.getElementById("enquiry")?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Home = ({ bannerImages = [] }) => {
  const location = useLocation();
  const [trades, setTrades] = useState([]);
  const [admissions, setAdmissions] = useState({});
  const [testimonials, setTestimonials] = useState([]);
  const [site, setSite] = useState({ placements: {} });

  const [enquiry, setEnquiry] = useState({ name: "", phone: "", trade: "" });
  const [enquirySent, setEnquirySent] = useState(false);
  const [enquiryError, setEnquiryError] = useState("");
  const [enquirySubmitting, setEnquirySubmitting] = useState(false);
  const [toasts, setToasts] = useState([]);
  const enquirySubmitLock = useRef(false);

  const showToast = useCallback((type, text) => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts((prev) => [{ id, type, text }, ...prev].slice(0, 3));
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
    }, 3400);
  }, []);

  useEffect(() => {
    Promise.all([
      fetchTrades(),
      fetchTestimonials().catch(() => []),
      fetchAdmissions().catch(() => ({})),
      fetchSiteSettings(),
    ])
      .then(([tradesData, testimonialsData, admissionsData, siteData]) => {
        setTrades(Array.isArray(tradesData) ? tradesData : []);
        setSite(siteData && typeof siteData === "object" ? siteData : {});
        setTestimonials(Array.isArray(testimonialsData) ? testimonialsData : []);
        setAdmissions(admissionsData && typeof admissionsData === "object" ? admissionsData : {});
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!location.hash) return undefined;
    if (location.hash === "#enquiry") {
      const t = setTimeout(() => scrollToEnquiry(), 120);
      return () => clearTimeout(t);
    }
    const id = location.hash.replace("#", "");
    const t = setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 100);
    return () => clearTimeout(t);
  }, [location.hash]);

  const safeBanners = Array.isArray(bannerImages) ? bannerImages : [];
  const banner1 = safeBanners?.[0]?.image || "";
  const banner2 = safeBanners?.[1]?.image || "";
  const bannerSrc = getAssetUrl(banner1);
  const banner2Src = getAssetUrl(banner2);

  const submitEnquiry = useCallback(
    async (e) => {
      e.preventDefault();
      if (enquirySubmitLock.current) return;
      enquirySubmitLock.current = true;
      setEnquirySent(false);
      setEnquiryError("");
      setEnquirySubmitting(true);
      try {
        await postEnquiryLead({
          name: enquiry.name,
          phone: enquiry.phone,
          trade: enquiry.trade,
        });
        setEnquirySent(true);
        showToast("success", "Enquiry submitted successfully.");
        setTimeout(() => setEnquirySent(false), 8000);
      } catch {
        setEnquiryError("Failed to submit. Please try again.");
        showToast("error", "Unable to submit enquiry.");
        /* network error — user can still call the college */
      } finally {
        setEnquirySubmitting(false);
        enquirySubmitLock.current = false;
      }
    },
    [enquiry, showToast]
  );

  const principalPhoto = getAssetUrl(site.principalImage || "");

  const admissionOpenTrim = (admissions.admissionOpenDate || "").trim();
  const admissionCloseTrim = (admissions.admissionCloseDate || "").trim();
  const academicYearTrim = (admissions.academicYear || "").trim();
  const showAdmissionDateCards = Boolean(admissionOpenTrim || admissionCloseTrim);

  const verificationStart = (admissions.verificationStartDate || "").trim();
  const verificationEnd = (admissions.verificationEndDate || "").trim();
  const showVerificationBlock = Boolean(verificationStart || verificationEnd);

  const admissionStatusMessage = useMemo(() => {
    if (!admissionOpenTrim && !admissionCloseTrim) {
      return academicYearTrim
        ? `Admission dates and eligibility details will be published here for next academic year ${academicYearTrim}.`
        : "Admission dates and eligibility details will be published here for the next academic year.";
    }
    return "Admission applications are started. Visit our college or click Apply Now for admissions.";
  }, [academicYearTrim, admissionOpenTrim, admissionCloseTrim]);

  const pdfUrl = (admissions?.pdfUrl || "").trim();
  const viewerUrl = pdfUrl
    ? `https://docs.google.com/gview?url=${encodeURIComponent(pdfUrl)}&embedded=true`
    : "";
  const tentativeCalendarPdfUrl = (admissions?.tentativeCalendarPdfUrl || "").trim();
  const tentativeCalendarViewerUrl = tentativeCalendarPdfUrl
    ? `https://docs.google.com/gview?url=${encodeURIComponent(tentativeCalendarPdfUrl)}&embedded=true`
    : "";

  const dateCardCount =
    (showAdmissionDateCards ? 2 : 0) + (showVerificationBlock ? 2 : 0);

  return (
    <div className="home-page marketing-home">
      <p className="container home-seo-intro font-semibold tracking-wide leading-relaxed">
        Government ITI Harihara — a leading skill training institute offering NCVT-aligned courses and
        practical education.
      </p>

      <section className="hero-section marketing-hero hero-gradient reveal-on-scroll">
        <div className="hero-blob hero-blob-a" aria-hidden />
        <div className="hero-blob hero-blob-b" aria-hidden />
        <div className="container hero-container">
          <div className="hero-content">
            <h1 className="hero-title hero-title-glow hero-title-typing hero-animate-title font-bold tracking-wide leading-relaxed">
              Government ITI Harihara
            </h1>
            <p className="hero-subtitle hero-animate-subtitle leading-relaxed">— Build Your Skilled Career</p>
            <div className="hero-cta-group">
              <button
                type="button"
                className={`${btnPrimary} bg-gradient-to-r from-pink-400 to-yellow-300`}
                onClick={scrollToEnquiry}
              >
                Apply Now
              </button>
              <a href={`tel:${PHONE_TEL}`} className={btnSecondary}>
                Call Now
              </a>
            </div>
            <p className="hero-trust-line">
              Government Approved | Experienced Faculty | Placement Assistance
            </p>
          </div>
          <div className="hero-image-wrapper banner-container hero-slider hero-slider-stack hero-animate-banner">
            {bannerSrc ? (
              <StableImage
                src={bannerSrc}
                alt="Government ITI College Harihar — GITI Harihara campus and training facilities"
                className="hero-image"
                width={1200}
                height={430}
                priority
              />
            ) : (
              <div className="hero-image hero-empty">No banner image uploaded yet.</div>
            )}
          </div>
        </div>
      </section>
      {banner2Src ? (
        <section className="hero-section marketing-hero reveal-on-scroll">
          <div className="container hero-container">
            <div className="hero-image-wrapper banner-container hero-slider hero-slider-stack">
              <StableImage
                src={banner2Src}
                alt="Government ITI College Harihar secondary banner"
                className="hero-image"
                width={1200}
                height={430}
              />
            </div>
          </div>
        </section>
      ) : null}

      <section className="section container trust-strip-section reveal-on-scroll" aria-label="Trust highlights">
        <div className="trust-strip">
          <div className="trust-strip-item">
            <FaCertificate className="trust-strip-icon" aria-hidden />
            <span>Government approved programs</span>
          </div>
          <div className="trust-strip-item">
            <FaUserGraduate className="trust-strip-icon" aria-hidden />
            <span>Industry-ready practical training</span>
          </div>
          <div className="trust-strip-item">
            <FaHandshake className="trust-strip-icon" aria-hidden />
            <span>Placement & career support</span>
          </div>
        </div>
      </section>

      <section id="about" className="section container why-section-block reveal-on-scroll">
        <h2 className="section-title-gradient text-center px-2">
          Why Choose Government ITI College Harihara
        </h2>
        <p className="mx-auto mb-8 max-w-3xl text-center font-semibold tracking-wide leading-relaxed text-slate-600">
          NCVT-approved trades, hands-on workshops, and placement-focused mentoring — built for rural youth
          ready for 4.0 industry.
        </p>
        <div className="glass-card why-copy-card relative rounded-2xl bg-white/30 backdrop-blur-lg border border-white/20 shadow-xl transition-all duration-300 hover:scale-105 before:absolute before:top-0 before:left-0 before:w-full before:h-[2px] before:bg-gradient-to-r before:from-pink-400 before:to-yellow-300 before:opacity-0 hover:before:opacity-100 before:transition shadow-md shadow-pink-100/30">
          <ul className="flex flex-col gap-4">
            <li className="flex gap-3 text-left font-semibold tracking-wide leading-relaxed text-slate-700">
              <FaCheckCircle className="mt-1 shrink-0 text-pink-500" aria-hidden />
              <span>
                Practical training led by experienced faculty in well-equipped workshops aligned to industry
                expectations.
              </span>
            </li>
            <li className="flex gap-3 text-left font-semibold tracking-wide leading-relaxed text-slate-700">
              <FaCertificate className="mt-1 shrink-0 text-yellow-500" aria-hidden />
              <span>
                Government-certified courses and disciplined skill practice so every learner gains confidence.
              </span>
            </li>
            <li className="flex gap-3 text-left font-semibold tracking-wide leading-relaxed text-slate-700">
              <FaHandshake className="mt-1 shrink-0 text-pink-500" aria-hidden />
              <span>
                Rooted in Davanagere district — strengthening industrial 4.0 skills across the region with career
                guidance.
              </span>
            </li>
          </ul>
        </div>
      </section>

      <section id="principal" className="section container principal-section reveal-on-scroll">
        <h2 className="section-title-gradient text-center">Principal Message</h2>
        <div className="principal-card glass-card relative rounded-2xl bg-white/30 backdrop-blur-lg border border-white/20 shadow-xl transition-all duration-300 hover:scale-105 before:absolute before:top-0 before:left-0 before:w-full before:h-[2px] before:bg-gradient-to-r before:from-pink-400 before:to-yellow-300 before:opacity-0 hover:before:opacity-100 before:transition shadow-md shadow-pink-100/25">
          {principalPhoto ? (
            <ProgressiveImage
              src={principalPhoto}
              alt="Principal at Government ITI Harihara"
              className="principal-photo"
              width={220}
              height={275}
              loading="lazy"
            />
          ) : (
            <div className="principal-photo principal-photo-placeholder">Principal image not uploaded</div>
          )}
          <div className="principal-copy">
            <h3 className="principal-name">Principal</h3>
            <blockquote className="principal-message font-semibold tracking-wide leading-relaxed text-slate-700">
              <p className="mb-4">
                A commitment to transforming rural youth into skilled professionals through NCVT (National
                Council for Vocational Training) approved courses.
              </p>
              <p className="mb-4">
                <span className="text-pink-600">Practical Excellence:</span> Emphasis on &quot;Learning by
                Doing,&quot; highlighting hands-on workshops for trades such as Electrician, Fitter, CNC,
                MEV, and Industrial Robotics , etc., .
              </p>
              <p className="mb-4">
                <span className="text-yellow-600">Industrial Readiness:</span> Preparing students for the
                modern industrial landscape with discipline, safety, and technical skills.
              </p>
              <p>
                <span className="text-pink-600">Placements:</span> Providing opportunities through Job Mails
                and Apprenticeships to ensure successful careers.
              </p>
            </blockquote>
          </div>
        </div>
      </section>

      <section id="results" className="section container placement-section reveal-on-scroll">
        <h2 className="section-title-gradient text-center">Our Results</h2>
        <div className="placement-static glass-card relative rounded-2xl bg-white/30 backdrop-blur-lg border border-white/20 shadow-xl transition-all duration-300 hover:scale-105 before:absolute before:top-0 before:left-0 before:w-full before:h-[2px] before:bg-gradient-to-r before:from-pink-400 before:to-yellow-300 before:opacity-0 hover:before:opacity-100 before:transition">
          <p className="placement-static-line">
            <strong>{Number(site.placements?.studentsTrained || 0)}+</strong> Students Trained
          </p>
          <p className="placement-static-line">
            <strong>{Number(site.placements?.studentsPlaced || 0)}+</strong> Students Placed
          </p>
          <p className="placement-static-line">
            <strong>{Number(site.placements?.studentsOfferedToHigherEducation || 0)}+</strong> Students Offered to
            Higher Education
          </p>
          <p className="placement-static-line">Strong Industry Exposure</p>
        </div>
      </section>

      <section id="admissions" className="section container reveal-on-scroll">
        <h2 className="section-title-gradient text-center">Admissions</h2>
        <div className="glass-card mx-auto max-w-4xl relative rounded-2xl bg-white/30 backdrop-blur-lg border border-white/20 shadow-xl transition-all duration-300 before:absolute before:top-0 before:left-0 before:w-full before:h-[2px] before:bg-gradient-to-r before:from-pink-400 before:to-yellow-300 before:opacity-0 hover:before:opacity-100 before:transition shadow-md shadow-yellow-100/40">
          <p className="mb-8 text-center text-lg font-bold leading-relaxed tracking-wide text-slate-800 md:text-xl">
            {admissionStatusMessage}
          </p>
          {dateCardCount > 0 ? (
            <div
              className={`mb-8 grid gap-4 sm:grid-cols-2 ${
                dateCardCount === 4 ? "xl:grid-cols-4" : "mx-auto max-w-2xl"
              }`}
            >
              {showAdmissionDateCards ? (
                <>
                  <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-4 text-center shadow-sm transition hover:shadow-md">
                    <p className="text-xs font-bold uppercase tracking-wide text-pink-500">Admission Start</p>
                    <p className="mt-2 text-lg font-bold text-slate-900">{admissionOpenTrim || "—"}</p>
                  </div>
                  <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-4 text-center shadow-sm transition hover:shadow-md">
                    <p className="text-xs font-bold uppercase tracking-wide text-yellow-600">Admission End</p>
                    <p className="mt-2 text-lg font-bold text-slate-900">{admissionCloseTrim || "—"}</p>
                  </div>
                </>
              ) : null}
              {showVerificationBlock ? (
                <>
                  <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-4 text-center shadow-sm transition hover:shadow-md">
                    <p className="text-xs font-bold uppercase tracking-wide text-pink-500">Verification Start Date</p>
                    <p className="mt-2 text-lg font-bold text-slate-900">{verificationStart || "—"}</p>
                  </div>
                  <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-4 text-center shadow-sm transition hover:shadow-md">
                    <p className="text-xs font-bold uppercase tracking-wide text-yellow-600">Verification End Date</p>
                    <p className="mt-2 text-lg font-bold text-slate-900">{verificationEnd || "—"}</p>
                  </div>
                </>
              ) : null}
            </div>
          ) : null}
          {pdfUrl ? (
            <>
              <iframe
                title="Admission PDF"
                src={viewerUrl}
                className="w-full max-w-5xl mx-auto h-[600px] rounded-xl border"
              />
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <a
                  href={pdfUrl}
                  download="Admission_Application_Form.pdf"
                  className={`${btnPrimary} shadow-md transition hover:scale-[1.02] hover:shadow-lg`}
                >
                  Download PDF
                </a>
                <button
                  type="button"
                  className={`${btnPrimary} shadow-md transition hover:scale-[1.02] hover:shadow-lg`}
                  onClick={scrollToEnquiry}
                >
                  Fill this Form
                </button>
              </div>
            </>
          ) : (
            <p className="text-center text-slate-500 font-medium tracking-wide leading-relaxed text-sm md:text-base">
              As soon admission document will comes here
            </p>
          )}
          {tentativeCalendarPdfUrl ? (
            <div className="mt-8">
              <h3 className="mb-3 text-center text-base font-semibold text-slate-900 md:text-lg">
                Tentative Calendar of Events
              </h3>
              <iframe
                title="Tentative Calendar of Events PDF"
                src={tentativeCalendarViewerUrl}
                className="w-full max-w-5xl mx-auto h-[600px] rounded-xl border"
              />
            </div>
          ) : null}
        </div>
      </section>

      <Testimonials items={testimonials} intervalMs={3800} />

      <section className="section container final-cta reveal-on-scroll rounded-3xl bg-gradient-to-r from-pink-400 to-yellow-300 px-6 py-12 shadow-xl ring-1 ring-white/20">
        <h2 className="final-cta-title text-white">Start Your Career Today</h2>
        <p className="final-cta-text text-white/95">
          Admissions open — limited seats. Secure your place at Government ITI College Harihar.
        </p>
        <div className="hero-cta-group final-cta-actions">
          <button type="button" className={btnOnGradient} onClick={scrollToEnquiry}>
            Apply Now
          </button>
          <a href={`tel:${PHONE_TEL}`} className={btnOutlineOnGradient}>
            Call Now
          </a>
        </div>
      </section>

      <section id="enquiry" className="section container home-enquiry-section reveal-on-scroll">
        <div className="glass-card home-enquiry-card relative rounded-2xl bg-white/30 backdrop-blur-lg border border-white/20 shadow-xl transition-all duration-300 hover:scale-105 before:absolute before:top-0 before:left-0 before:w-full before:h-[2px] before:bg-gradient-to-r before:from-pink-400 before:to-yellow-300 before:opacity-0 hover:before:opacity-100 before:transition">
          <h2 className="section-title-gradient text-center">Admission enquiry</h2>
          <div className="section-lead home-enquiry-lead mx-auto max-w-md space-y-3 text-center font-semibold leading-relaxed tracking-wide">
            <p>Share your details — we will get back to you.</p>
            <p>
              Call us:{" "}
              <a href={`tel:${PHONE_TEL}`} className="text-pink-600 underline-offset-2 hover:underline">
                {PHONE_DISPLAY}
              </a>{" "}
              during office hours.
            </p>
          </div>
          <form className="home-enquiry-form mx-auto max-w-md text-center" onSubmit={submitEnquiry}>
            <label className="home-enquiry-label text-left">
              Name
              <input
                type="text"
                name="name"
                value={enquiry.name}
                onChange={(e) => setEnquiry((x) => ({ ...x, name: e.target.value }))}
                required
                autoComplete="name"
                className={inputField}
              />
            </label>
            <label className="home-enquiry-label text-left">
              Phone
              <input
                type="tel"
                name="phone"
                value={enquiry.phone}
                onChange={(e) => setEnquiry((x) => ({ ...x, phone: e.target.value }))}
                required
                autoComplete="tel"
                className={inputField}
              />
            </label>
            <label className="home-enquiry-label text-left">
              Trade
              <select
                name="trade"
                value={enquiry.trade}
                onChange={(e) => setEnquiry((x) => ({ ...x, trade: e.target.value }))}
                className={inputField}
              >
                <option value="">Select a trade</option>
                {trades.map((t) => (
                  <option key={t.id} value={t.name}>
                    {t.name}
                  </option>
                ))}
                <option value="Other">Other / Not sure</option>
              </select>
            </label>
            <button
              type="submit"
              className={`${btnPrimary} ${enquirySubmitting ? "button-loading" : ""} home-enquiry-submit w-full sm:w-auto`}
              disabled={enquirySubmitting}
              aria-busy={enquirySubmitting}
            >
              {enquirySubmitting ? (
                <>
                  <span className="button-spinner" aria-hidden />
                  Submitting…
                </>
              ) : (
                "Submit enquiry"
              )}
            </button>
            {enquirySent ? (
              <p className="home-enquiry-sent" role="status">
                Your enquiry has been submitted successfully. Our team will contact you soon.
              </p>
            ) : null}
            {enquiryError ? (
              <p className="text-sm font-medium text-red-600" role="alert">
                {enquiryError}
              </p>
            ) : null}
          </form>
        </div>
      </section>

      <div className="home-sticky-cta">
        <button
          type="button"
          className="home-sticky-cta-btn inline-flex w-full items-center justify-center rounded-full bg-gradient-to-r from-pink-400 to-yellow-300 px-6 py-3.5 text-base font-semibold text-white shadow-lg ring-2 ring-pink-200/50 transition hover:scale-[1.02] hover:shadow-xl active:scale-[0.99]"
          onClick={scrollToEnquiry}
        >
          Apply Now
        </button>
      </div>
      <ToastStack toasts={toasts} />

    </div>
  );
};

export default Home;
